/**
 * DSL Lexer - Tokenizes DSL input with position information
 */

/** One lexed piece of DSL source: what it is, and what it said. */
export interface Token {
  type: 'identifier' | 'string' | 'number' | 'punct';
  value: string;
}

/** A token that also knows where it came from, so a diagnostic can point at it. */
export interface PositionedToken extends Token {
  start: number;
  end: number;
  line: number;
  col: number;
  error?: string;
}

/**
 * Tokenize DSL source with position information for diagnostics
 */
export function lexWithPos(src: string): PositionedToken[] {
  const out: PositionedToken[] = [];
  let i = 0;
  const n = src.length;
  let line = 0;
  let col = 0;

  function push(
    type: Token['type'],
    value: string,
    start: number,
    end: number,
    line0: number,
    col0: number,
    error?: string,
  ) {
    out.push({
      type,
      value,
      start,
      end,
      line: line0,
      col: col0,
      ...(error ? { error } : {}),
    });
  }

  function isAlpha(c: string) {
    return /[A-Za-z_]/.test(c);
  }

  function isAlnum(c: string) {
    return /[A-Za-z0-9_]/.test(c);
  }

  while (i < n) {
    const c = src.charAt(i);

    if (c === '\n') {
      i++;
      line++;
      col = 0;
      continue;
    }

    if (/\s/.test(c)) {
      i++;
      col++;
      continue;
    }

    if (c === '#') {
      while (i < n && src[i] !== '\n') {
        i++;
        col++;
      }
      continue;
    }

    if (c === '"' || c === "'") {
      const quote = c;
      const start = i;
      const line0 = line;
      const col0 = col;
      i++;
      col++;
      let val = '';

      while (i < n && src[i] !== quote) {
        if (src[i] === '\\' && i + 1 < n) {
          const escaped = src.charAt(i + 1);
          const escapes: Record<string, string> = {
            n: '\n',
            r: '\r',
            t: '\t',
            '\\': '\\',
            '"': '"',
            "'": "'",
          };
          val += escapes[escaped] ?? escaped;
          i += 2;
          col += 2;
          continue;
        }
        if (src[i] === '\n') {
          line++;
          col = 0;
          val += '\n';
          i++;
          continue;
        }
        val += src[i];
        i++;
        col++;
      }

      const terminated = src[i] === quote;
      if (terminated) {
        i++;
        col++;
      }

      push(
        'string',
        val,
        start,
        i,
        line0,
        col0,
        terminated ? undefined : 'Unterminated string literal',
      );
      continue;
    }

    if (
      /[0-9]/.test(c) ||
      (c === '-' && i + 1 < n && /[0-9]/.test(src.charAt(i + 1)))
    ) {
      const start = i;
      const line0 = line;
      const col0 = col;
      const numberStart = i;
      if (src[i] === '-') i++;
      while (i < n && /[0-9]/.test(src.charAt(i))) i++;
      if (src[i] === '.' && i + 1 < n && /[0-9]/.test(src.charAt(i + 1))) {
        i++;
        while (i < n && /[0-9]/.test(src.charAt(i))) i++;
      }
      if ((src[i] === 'e' || src[i] === 'E') && i + 1 < n) {
        const exponentStart = i;
        i++;
        if (src[i] === '+' || src[i] === '-') i++;
        const digitsStart = i;
        while (i < n && /[0-9]/.test(src.charAt(i))) i++;
        if (i === digitsStart) i = exponentStart;
      }
      const val = src.slice(numberStart, i);
      col += i - start;

      push('number', val, start, i, line0, col0);
      continue;
    }

    if (isAlpha(c)) {
      const start = i;
      const line0 = line;
      const col0 = col;
      let val = c;
      i++;
      col++;

      while (i < n && isAlnum(src.charAt(i))) {
        val += src.charAt(i);
        i++;
        col++;
      }

      push('identifier', val, start, i, line0, col0);
      continue;
    }

    if ('().,:;{}'.includes(c)) {
      push('punct', c, i, i + 1, line, col);
      i++;
      col++;
      continue;
    }

    push('punct', c, i, i + 1, line, col);
    i++;
    col++;
  }

  return out;
}
